/**
 * Conversation Flow Step Definitions
 * Defines all steps in the buyer/seller conversation flows
 */

import type { ConversationStep, StepDefinition, ConversationState } from '../types'
import { getDeflectionResponse } from './aiDetection'

// All conversation steps are already defined in shared/types/ConversationState.ts
// This file defines the step definitions (message templates, validation, next step logic)

/**
 * Helper to get agent name from state
 */
function getAgentName(state: ConversationState): string {
  return state.clientConfig?.agentName || 'your agent'
}

/**
 * Map of all step definitions
 */
export const STEP_DEFINITIONS: Record<ConversationStep, StepDefinition> = {
  // WELCOME
  welcome: {
    message: (state) => `Hey there! 👋 I'm ${state.botName}, I help ${getAgentName(state)} with scheduling. Are you looking to buy or sell?`,
    inputType: 'buttons',
    options: [
      { label: '🏠 Buying', value: 'buying' },
      { label: '💰 Selling', value: 'selling' },
      { label: 'Just have a question', value: 'question' },
    ],
    nextStep: (value) => {
      if (value === 'buying') return 'buyer_price_range'
      if (value === 'selling') return 'seller_address'
      return 'question_input' // Route questions to question_input
    },
  },

  // FREEFORM WELCOME (for storefront sales chat)
  freeform_welcome: {
    message: () => `Hey there! 👋 What can I help you with today?`,
    inputType: 'text',
    validator: (value) => {
      if (!value || value.trim().length < 2) {
        return 'Please enter your question'
      }
      return null
    },
    nextStep: () => 'freeform_welcome', // Stay in freeform mode, handled by Claude AI
  },

  // BUY OR SELL (fallback if needed)
  buy_or_sell: {
    message: () => `Cool! So are you looking to buy or sell?`,
    inputType: 'buttons',
    options: [
      { label: '🏠 Buy', value: 'buy' },
      { label: '💰 Sell', value: 'sell' },
    ],
    nextStep: (value) => {
      if (value === 'buy') return 'buyer_price_range'
      return 'seller_address'
    },
  },

  // ==================== BUYER PATH ====================

  buyer_price_range: {
    message: () => `What's your budget looking like?`,
    inputType: 'buttons',
    options: [
      { label: 'Under $800K', value: 'under_800k' },
      { label: '$800K - $1.2M', value: '800k_1.2m' },
      { label: '$1.2M - $2M', value: '1.2m_2m' },
      { label: '$2M+', value: 'over_2m' },
    ],
    nextStep: () => 'buyer_cities',
  },

  buyer_cities: {
    message: () => `Which East Bay cities are you looking at?`,
    inputType: 'buttons',
    options: [
      { label: 'Castro Valley', value: 'castro_valley' },
      { label: 'Fremont', value: 'fremont' },
      { label: 'Hayward', value: 'hayward' },
      { label: 'San Leandro', value: 'san_leandro' },
      { label: 'Union City', value: 'union_city' },
      { label: 'Other', value: 'other' },
    ],
    nextStep: () => 'buyer_preapproval',
  },

  buyer_preapproval: {
    message: () => `Are you pre-approved for a mortgage yet?`,
    inputType: 'buttons',
    options: [
      { label: "✅ Yes, I'm pre-approved", value: 'yes_preapproved', emoji: '✅' },
      { label: '📋 Getting pre-approved now', value: 'getting_preapproved', emoji: '📋' },
      { label: '❌ Not yet', value: 'not_yet', emoji: '❌' },
    ],
    nextStep: () => 'buyer_timeline',
  },

  buyer_timeline: {
    message: () => `When are you looking to buy?`,
    inputType: 'buttons',
    options: [
      { label: 'Ready now (0-30 days)', value: 'ready_now' },
      { label: '1-3 months', value: '1_3_months' },
      { label: '3-6 months', value: '3_6_months' },
      { label: 'Just browsing', value: 'just_browsing' },
    ],
    nextStep: () => 'buyer_push_calendar',
  },

  buyer_push_calendar: {
    message: (state) => `Perfect! ${getAgentName(state)}'s calendar is pretty open this week. Want me to grab you a time slot? Takes like 30 seconds.`,
    inputType: 'buttons',
    options: [
      { label: '📅 Yeah, let\'s book it', value: 'book_it', emoji: '📅' },
      { label: 'Not yet, just get my info', value: 'get_info' },
    ],
    nextStep: (value) => {
      if (value === 'book_it') return 'buyer_collect_name' // Will show Calendly after name
      return 'buyer_collect_name'
    },
  },

  buyer_collect_name: {
    message: (state) => {
      const wantsToBook = state.leadData.wantsCalendar === true
      if (wantsToBook) {
        return `Awesome! What's your name and phone so ${getAgentName(state)} has it before the call?`
      }
      return `No worries! What's your name?`
    },
    inputType: 'text',
    validator: (value) => {
      if (!value || value.trim().length < 2) {
        return 'Please enter your name'
      }
      return null
    },
    nextStep: () => 'buyer_collect_phone',
  },

  buyer_collect_phone: {
    message: (state) => `Thanks, ${String(state.leadData.name || '')}! And your number?`,
    inputType: 'phone',
    validator: (value) => {
      const phoneRegex = /^[\d\s\-()]+$/
      if (!value || !phoneRegex.test(value)) {
        return 'Please enter a valid phone number'
      }
      if (value.replace(/\D/g, '').length < 10) {
        return 'Phone number must be at least 10 digits'
      }
      return null
    },
    nextStep: () => 'buyer_collect_email',
  },

  buyer_collect_email: {
    message: () => `Last thing - your email?`,
    inputType: 'email',
    validator: (value) => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!value || !emailRegex.test(value)) {
        return 'Please enter a valid email address'
      }
      return null
    },
    nextStep: (_value, state) => {
      // If they already agreed to book, show Calendly now
      if (state.leadData.wantsCalendar === true) {
        return 'buyer_show_calendly'
      }
      // Otherwise, push for calendar again
      return 'buyer_push_calendar_again'
    },
  },

  buyer_show_calendly: {
    message: (state) => `Perfect! Here's ${getAgentName(state)}'s calendar - pick any time that works for you:`,
    inputType: 'calendly',
    nextStep: () => 'thank_you',
  },

  buyer_push_calendar_again: {
    message: (state) => `Got it! You know what, ${getAgentName(state)} actually has a slot open tomorrow at 2pm. Want me to lock that in for you? Way easier than playing phone tag.`,
    inputType: 'buttons',
    options: [
      { label: '📅 Sure, book it', value: 'book_it', emoji: '📅' },
      { label: 'Nah, he can just call me', value: 'no_calendar' },
    ],
    nextStep: (value, _state) => {
      if (value === 'book_it') return 'buyer_show_calendly'
      return 'thank_you'
    },
  },

  // ==================== SELLER PATH ====================

  seller_address: {
    message: () => `What's the address of the place you're selling?`,
    inputType: 'text',
    validator: (value) => {
      if (!value || value.trim().length < 10) {
        return 'Please enter a full street address'
      }
      return null
    },
    nextStep: () => 'seller_timeline',
  },

  seller_timeline: {
    message: () => `When are you looking to sell?`,
    inputType: 'buttons',
    options: [
      { label: 'ASAP (0-30 days)', value: 'asap' },
      { label: '1-3 months', value: '1_3_months' },
      { label: '3-6 months', value: '3_6_months' },
      { label: 'Just exploring options', value: 'exploring' },
    ],
    nextStep: () => 'seller_agent_experience',
  },

  seller_agent_experience: {
    message: () => `Have you sold a home before?`,
    inputType: 'buttons',
    options: [
      { label: 'Yes, multiple times', value: 'multiple' },
      { label: 'Yes, once', value: 'once' },
      { label: 'No, first time', value: 'first_time' },
    ],
    nextStep: () => 'seller_push_calendar',
  },

  seller_push_calendar: {
    message: (state) => `Cool! ${getAgentName(state)} would love to chat about your property. Want me to get you on their calendar this week?`,
    inputType: 'buttons',
    options: [
      { label: '📅 Yeah, let\'s book it', value: 'book_it', emoji: '📅' },
      { label: 'Not yet, just get my info', value: 'get_info' },
    ],
    nextStep: (value) => {
      if (value === 'book_it') return 'seller_collect_name'
      return 'seller_collect_name'
    },
  },

  seller_collect_name: {
    message: (state) => {
      const wantsToBook = state.leadData.wantsCalendar === true
      if (wantsToBook) {
        return `Awesome! What's your name?`
      }
      return `No worries! What's your name?`
    },
    inputType: 'text',
    validator: (value) => {
      if (!value || value.trim().length < 2) {
        return 'Please enter your name'
      }
      return null
    },
    nextStep: () => 'seller_collect_phone',
  },

  seller_collect_phone: {
    message: (state) => `Thanks, ${String(state.leadData.name || '')}! What's your phone number?`,
    inputType: 'phone',
    validator: (value) => {
      const phoneRegex = /^[\d\s\-()]+$/
      if (!value || !phoneRegex.test(value)) {
        return 'Please enter a valid phone number'
      }
      if (value.replace(/\D/g, '').length < 10) {
        return 'Phone number must be at least 10 digits'
      }
      return null
    },
    nextStep: () => 'seller_collect_email',
  },

  seller_collect_email: {
    message: () => `And your email?`,
    inputType: 'email',
    validator: (value) => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!value || !emailRegex.test(value)) {
        return 'Please enter a valid email address'
      }
      return null
    },
    nextStep: (_value, state) => {
      if (state.leadData.wantsCalendar === true) {
        return 'seller_show_calendly'
      }
      return 'seller_push_calendar_again'
    },
  },

  seller_show_calendly: {
    message: (state) => `Perfect! Here's ${getAgentName(state)}'s calendar - pick any time that works:`,
    inputType: 'calendly',
    nextStep: () => 'thank_you',
  },

  seller_push_calendar_again: {
    message: (state) => `Got it! ${getAgentName(state)}'s actually got some time tomorrow afternoon. Want me to book you in? Takes 2 seconds.`,
    inputType: 'buttons',
    options: [
      { label: '📅 Sure, book it', value: 'book_it', emoji: '📅' },
      { label: 'Nah, he can just call me', value: 'no_calendar' },
    ],
    nextStep: (value, _state) => {
      if (value === 'book_it') return 'seller_show_calendly'
      return 'thank_you'
    },
  },

  // ==================== QUESTION HANDLING ====================

  question_input: {
    message: (state) => `What's your question? I'll do my best to help or get you connected with ${getAgentName(state)}.`,
    inputType: 'text',
    validator: (value) => {
      if (!value || value.trim().length < 3) {
        return 'Please enter your question'
      }
      return null
    },
    nextStep: () => 'question_deflect',
  },

  question_deflect: {
    message: (state) =>
      `That's a great question! Honestly, ${getAgentName(state)} can answer that way better than me. Want to grab a quick time on their calendar?`,
    inputType: 'buttons',
    options: [
      { label: '📅 Yeah, let\'s book it', value: 'book_it', emoji: '📅' },
      { label: 'Just get my info for now', value: 'get_info' },
    ],
    nextStep: (value) => {
      if (value === 'book_it') return 'buy_or_sell'
      return 'buy_or_sell'
    },
  },

  ai_question_deflect: {
    message: (state) => getDeflectionResponse(state),
    inputType: 'buttons',
    options: [
      { label: '📅 Sure, book it', value: 'book_it', emoji: '📅' },
      { label: 'Just get my info', value: 'get_info' },
    ],
    nextStep: (value) => {
      if (value === 'book_it') return 'buy_or_sell'
      return 'buy_or_sell'
    },
  },

  // ==================== THANK YOU ====================

  thank_you: {
    message: (state) => {
      const name = String(state.leadData.name || 'there')
      const booked = state.leadData.scheduledCall === true
      const agentName = getAgentName(state)

      if (booked) {
        return `Perfect, ${name}! You're all set. ${agentName} will hit you up at the scheduled time. Can't wait! 🎉`
      }
      return `Thanks ${name}! ${agentName} will give you a call soon. Have a great day! 👋`
    },
    inputType: 'text', // No input needed, just display message
    nextStep: () => 'thank_you', // End of conversation
  },
}

/**
 * Get step definition for a given step
 */
export function getStepDefinition(step: ConversationStep): StepDefinition {
  const definition = STEP_DEFINITIONS[step]
  if (!definition) {
    throw new Error(`Unknown conversation step: ${step}`)
  }
  return definition
}

/**
 * Validate that all steps are defined and reachable
 */
export function validateStepDefinitions(): { valid: boolean; errors: string[] } {
  const errors: string[] = []
  const allSteps: ConversationStep[] = Object.keys(STEP_DEFINITIONS) as ConversationStep[]

  // Check that all steps have required fields
  for (const step of allSteps) {
    const def = STEP_DEFINITIONS[step]

    if (!def.message) {
      errors.push(`Step ${step} missing message function`)
    }

    if (!def.inputType) {
      errors.push(`Step ${step} missing inputType`)
    }

    if (!def.nextStep) {
      errors.push(`Step ${step} missing nextStep function`)
    }

    // Buttons input type must have options
    if (def.inputType === 'buttons' && (!def.options || def.options.length === 0)) {
      errors.push(`Step ${step} has buttons inputType but no options`)
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  }
}
